import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import OnboardingAutomationPanel from '../components/OnboardingAutomationPanel';
import PeopleWorkloadPanel from '../components/PeopleWorkloadPanel';
import NotionApiAutomationPanel from '../components/NotionApiAutomationPanel';
import { api } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

interface NotionPage {
  id: string;
  title: string;
  icon: string | null;
  url: string;
  created_time: string;
  last_edited_time: string;
  archived: boolean;
}

interface NotionRow {
  id: string;
  url: string;
  created_time: string;
  last_edited_time: string;
  properties: Record<string, any>;
}

interface NotionDatabase {
  id: string;
  title: string;
  url: string;
  icon: string | null;
  created_time: string;
  last_edited_time: string;
  properties: Record<string, string>;
  row_count: number | null;
  rows: NotionRow[];
}

interface WorkspaceSummary {
  total_pages: number;
  total_databases: number;
  total_rows: number;
}

type NotionSection = 'workspace' | 'onboarding-automation' | 'people-workload' | 'notion-api-automation';

const NotionDashboardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeSection: NotionSection = location.pathname.endsWith('/onboarding-automation')
    ? 'onboarding-automation'
    : location.pathname.endsWith('/people-workload')
      ? 'people-workload'
      : location.pathname.endsWith('/notion-api-automation')
        ? 'notion-api-automation'
        : 'workspace';

  const [pages, setPages] = useState<NotionPage[]>([]);
  const [databases, setDatabases] = useState<NotionDatabase[]>([]);
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [configured, setConfigured] = useState(true);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'pages' | 'databases'>('overview');
  const [expandedDb, setExpandedDb] = useState<string | null>(null);
  const [loadingRows, setLoadingRows] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadWorkspace();
  }, []);

  const loadWorkspace = async () => {
    setLoading(true);
    setError(null);
    try {
      const statusRes = await api.get('/notion/status');
      if (!statusRes.data.configured) {
        setConfigured(false);
        setLoading(false);
        return;
      }
      setConfigured(true);

      const res = await api.get('/notion/workspace');
      if (res.data.success) {
        setPages(res.data.data.pages);
        setDatabases(res.data.data.databases);
        setSummary(res.data.data.summary);
        setIsCached(res.data.cached || false);
        setSyncedAt(res.data.synced_at || null);
      } else {
        setError(res.data.error);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    setError(null);
    try {
      const res = await api.post('/notion/sync');
      if (res.data.success) {
        setPages(res.data.data.pages);
        setDatabases(res.data.data.databases);
        setSummary(res.data.data.summary);
        setIsCached(true);
        setSyncedAt(res.data.synced_at || new Date().toISOString());
        setSyncMessage(res.data.message);
        // Clear expanded db since rows might be stale
        setExpandedDb(null);
      } else {
        setError(res.data.error);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleExpandDb = async (dbId: string) => {
    if (expandedDb === dbId) {
      setExpandedDb(null);
      return;
    }
    setExpandedDb(dbId);

    // Check if rows already loaded
    const db = databases.find(d => d.id === dbId);
    if (db && db.rows.length > 0) return;

    // Fetch rows on-demand
    setLoadingRows(dbId);
    try {
      const res = await api.get(`/notion/database/${dbId}`);
      if (res.data.success) {
        setDatabases(prev => prev.map(d =>
          d.id === dbId ? { ...d, rows: res.data.rows, row_count: res.data.count } : d
        ));
      }
    } catch (err: any) {
      console.error('Failed to load rows:', err);
    } finally {
      setLoadingRows(null);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDateShort = (iso: string | null) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderPropertyValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ') || '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // --- Chart data ---
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  const dbByColumnCount = useMemo(() => {
    // Group databases by number of columns
    const groups: Record<string, number> = {};
    databases.forEach(db => {
      const colCount = Object.keys(db.properties).length;
      const bucket = colCount <= 3 ? '1-3' : colCount <= 6 ? '4-6' : colCount <= 10 ? '7-10' : '11+';
      groups[bucket] = (groups[bucket] || 0) + 1;
    });
    return Object.entries(groups).map(([name, value]) => ({ name: `${name} cols`, value }));
  }, [databases]);

  const dbsByTitle = useMemo(() => {
    // Group databases by title (shows duplicates like "Resources")
    const groups: Record<string, number> = {};
    databases.forEach(db => {
      const name = db.title.replace(/📂\s*/, '').trim() || '(untitled)';
      groups[name] = (groups[name] || 0) + 1;
    });
    return Object.entries(groups)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name: name.length > 20 ? name.slice(0, 18) + '...' : name, count }));
  }, [databases]);

  const activityByMonth = useMemo(() => {
    // Pages + databases grouped by last_edited month
    const months: Record<string, { pages: number; databases: number }> = {};
    const addToMonth = (iso: string | null, type: 'pages' | 'databases') => {
      if (!iso) return;
      const d = new Date(iso);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!months[key]) months[key] = { pages: 0, databases: 0 };
      months[key][type]++;
    };
    pages.forEach(p => addToMonth(p.last_edited_time, 'pages'));
    databases.forEach(db => addToMonth(db.last_edited_time, 'databases'));
    return Object.entries(months)
      .sort()
      .slice(-12)
      .map(([month, data]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        pages: data.pages,
        databases: data.databases,
      }));
  }, [pages, databases]);

  const propertyTypes = useMemo(() => {
    // Count of each property type across all databases
    const types: Record<string, number> = {};
    databases.forEach(db => {
      Object.values(db.properties).forEach(t => {
        types[t] = (types[t] || 0) + 1;
      });
    });
    return Object.entries(types)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [databases]);

  const filteredPages = pages.filter(p => !search || p.title.toLowerCase().includes(search.toLowerCase()));
  const filteredDatabases = databases.filter(db => !search || db.title.toLowerCase().includes(search.toLowerCase()));

  const getVisibleColumns = (db: NotionDatabase): string[] => {
    if (db.rows.length === 0) return Object.keys(db.properties);
    const allProps = Object.keys(db.properties);
    const titleCol = allProps.find(p => db.properties[p] === 'title') || allProps[0];
    const otherCols = allProps.filter(p => p !== titleCol);
    return [titleCol, ...otherCols].slice(0, 8);
  };

  const renderRowsTable = (db: NotionDatabase) => {
    if (loadingRows === db.id) {
      return (
        <div className="border-t border-gray-100 px-5 py-6 text-center">
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="text-sm">Loading rows...</span>
          </div>
        </div>
      );
    }
    if (db.rows.length === 0) {
      return (
        <div className="border-t border-gray-100 px-5 py-6 text-center text-gray-400 text-sm">
          No rows in this database
        </div>
      );
    }
    const cols = getVisibleColumns(db);
    return (
      <div className="border-t border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {cols.map(col => (
                <th key={col} className="text-left px-4 py-2 font-medium text-gray-600 whitespace-nowrap">
                  {col}
                  <span className="text-xs text-gray-400 ml-1">({db.properties[col]})</span>
                </th>
              ))}
              <th className="text-left px-4 py-2 font-medium text-gray-600">Updated</th>
            </tr>
          </thead>
          <tbody>
            {db.rows.map(row => (
              <tr key={row.id} className="border-t border-gray-50 hover:bg-gray-50">
                {cols.map((col, i) => (
                  <td key={col} className="px-4 py-2 text-gray-700 max-w-xs truncate">
                    {i === 0 ? (
                      <a href={row.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                        {renderPropertyValue(row.properties[col]) || '(untitled)'}
                      </a>
                    ) : (
                      <span className={`${
                        ['Done', 'Complete', 'Published'].includes(row.properties[col]) ? 'text-emerald-600 font-medium'
                        : ['In Progress', 'In progress'].includes(row.properties[col]) ? 'text-blue-600 font-medium'
                        : ['Not Started', 'Not started'].includes(row.properties[col]) ? 'text-gray-400' : ''
                      }`}>
                        {renderPropertyValue(row.properties[col])}
                      </span>
                    )}
                  </td>
                ))}
                <td className="px-4 py-2 text-gray-400 text-xs whitespace-nowrap">{formatDateShort(row.last_edited_time)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {db.row_count !== null && db.row_count >= 200 && (
          <div className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
            Showing first 200 rows. <a href={db.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">View all in Notion</a>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notion Dashboard</h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-gray-500">Your Notion workspace content</p>
              {syncedAt && (
                <span className="text-xs text-gray-400">Last synced: {formatDate(syncedAt)}</span>
              )}
            </div>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {syncing ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {syncing ? 'Syncing...' : 'Sync from Notion'}
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-6 -mb-px">
            <button
              onClick={() => navigate('/notion-dashboard')}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeSection === 'workspace'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Workspace
            </button>
            <button
              onClick={() => navigate('/notion-dashboard/onboarding-automation')}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeSection === 'onboarding-automation'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Onboarding Automation
            </button>
            <button
              onClick={() => navigate('/notion-dashboard/people-workload')}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeSection === 'people-workload'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              People Workload
            </button>
            <button
              onClick={() => navigate('/notion-dashboard/notion-api-automation')}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeSection === 'notion-api-automation'
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notion API Automation
            </button>
          </nav>
        </div>

        {activeSection === 'onboarding-automation' && <OnboardingAutomationPanel />}
        {activeSection === 'people-workload' && <PeopleWorkloadPanel />}
        {activeSection === 'notion-api-automation' && <NotionApiAutomationPanel />}

        {activeSection === 'workspace' && (<>

        {/* Sync message */}
        {syncMessage && (
          <div className="mb-4 p-3 rounded-lg text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-between">
            <span>{syncMessage}</span>
            <button onClick={() => setSyncMessage(null)} className="text-current opacity-50 hover:opacity-100">&times;</button>
          </div>
        )}

        {/* Not configured */}
        {!configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <p className="text-amber-700 font-medium">Notion API key is not configured.</p>
            <p className="text-amber-600 text-sm mt-1">Add NOTION_API_KEY to your .env file to connect.</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
            {error}
            <button onClick={() => setError(null)} className="float-right text-current opacity-50 hover:opacity-100">&times;</button>
          </div>
        )}

        {/* Loading */}
        {loading && configured && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <svg className="w-8 h-8 animate-spin text-gray-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-gray-500">Loading workspace...</p>
          </div>
        )}

        {/* Content */}
        {!loading && configured && summary && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Pages</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{summary.total_pages}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Databases</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{summary.total_databases}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider">
                  {isCached ? 'Cached' : 'Status'}
                </p>
                <p className="text-lg font-semibold text-emerald-600 mt-1">
                  {isCached ? 'Loaded from cache' : 'Live from Notion'}
                </p>
              </div>
            </div>

            {/* Tabs + Search */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
                {(['overview', 'analytics', 'pages', 'databases'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-5 py-2 text-sm font-medium capitalize transition-colors ${
                      activeTab === tab ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg w-64 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              />
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-3">
                {filteredDatabases.map(db => (
                  <div key={db.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div
                      className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => handleExpandDb(db.id)}
                    >
                      <div className="flex items-center gap-3">
                        {db.icon && <span className="text-xl">{db.icon}</span>}
                        <div>
                          <h3 className="font-semibold text-gray-900">{db.title}</h3>
                          <p className="text-xs text-gray-500">
                            {db.row_count !== null ? `${db.row_count} rows` : 'Click to load rows'} | {Object.keys(db.properties).length} columns | Updated {formatDateShort(db.last_edited_time)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <a
                          href={db.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                          onClick={e => e.stopPropagation()}
                        >
                          Open in Notion
                        </a>
                        {loadingRows === db.id ? (
                          <svg className="w-5 h-5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform ${expandedDb === db.id ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {expandedDb === db.id && renderRowsTable(db)}
                  </div>
                ))}
                {filteredDatabases.length === 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
                    No databases found.
                  </div>
                )}
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                {/* Row 1: Activity Timeline + Top Databases */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Activity Timeline (Last 12 Months)</h3>
                    {activityByMonth.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={activityByMonth}>
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="pages" fill="#3b82f6" name="Pages" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="databases" fill="#10b981" name="Databases" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-12">No activity data</p>
                    )}
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Top Databases by Count</h3>
                    {dbsByTitle.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={dbsByTitle} layout="vertical">
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#8b5cf6" name="Instances" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-12">No database data</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Property Types + Column Distribution */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Property Types Used</h3>
                    {propertyTypes.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={propertyTypes} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {propertyTypes.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-12">No property data</p>
                    )}
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Database Schema Complexity</h3>
                    {dbByColumnCount.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={dbByColumnCount} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                            {dbByColumnCount.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-12">No schema data</p>
                    )}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">{pages.length}</p>
                      <p className="text-xs text-blue-600">Standalone Pages</p>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-700">{databases.length}</p>
                      <p className="text-xs text-emerald-600">Databases</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-700">
                        {databases.reduce((sum, db) => sum + Object.keys(db.properties).length, 0)}
                      </p>
                      <p className="text-xs text-purple-600">Total Properties</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-700">
                        {new Set(databases.map(db => db.title)).size}
                      </p>
                      <p className="text-xs text-amber-600">Unique DB Names</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pages Tab */}
            {activeTab === 'pages' && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {filteredPages.map(page => (
                    <a
                      key={page.id}
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg w-6 text-center">{page.icon || '📄'}</span>
                        <div>
                          <p className="font-medium text-gray-900">{page.title || '(untitled)'}</p>
                          {page.archived && <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Archived</span>}
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(page.last_edited_time)}</span>
                    </a>
                  ))}
                  {filteredPages.length === 0 && (
                    <div className="px-5 py-8 text-center text-gray-400 text-sm">No pages found</div>
                  )}
                </div>
              </div>
            )}

            {/* Databases Tab */}
            {activeTab === 'databases' && (
              <div className="space-y-4">
                {filteredDatabases.map(db => (
                  <div key={db.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-5 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {db.icon && <span className="text-xl">{db.icon}</span>}
                          <div>
                            <h3 className="font-semibold text-gray-900">{db.title}</h3>
                            <p className="text-xs text-gray-500">
                              {db.row_count !== null ? `${db.row_count} rows` : 'Rows not loaded'} | Created {formatDateShort(db.created_time)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleExpandDb(db.id)}
                            className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1"
                          >
                            {expandedDb === db.id ? 'Hide rows' : 'Load rows'}
                          </button>
                          <a href={db.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                            Open in Notion
                          </a>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(db.properties).map(([name, type]) => (
                          <span key={name} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                            <span className="font-medium">{name}</span>
                            <span className="text-gray-400">({type})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    {expandedDb === db.id && renderRowsTable(db)}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        </>)}
      </div>
    </Layout>
  );
};

export default NotionDashboardPage;
